<script lang="ts">
  import { authState, getIdentity, invalidateServerToken } from '$lib/auth.svelte'
  import { refreshServerAccess } from '$lib/server-access.svelte'
  import { openGrantAccess } from '$lib/grant-access.svelte'
  import { servers as serversStore } from '$lib/store'
  import {
    TRANSLATION_SOURCE_LANGUAGES,
    DEFAULT_TRANSLATION_ELIGIBLE_LANGUAGES,
  } from '$lib/apis/library/api'
  import {
    deleteServer,
    getServers,
    getServerSettings,
    renameServer,
    setServerUserRole,
    syncServerUsers,
    getBackupSettings,
    updateBackupSettings,
    triggerBackup,
    deleteBackup,
    downloadBackup,
    getTranslationCacheSettings,
    updateTranslationCacheSettings,
    testTranslationCacheMit,
    getNovelTranslationSettings,
    updateNovelTranslationSettings,
    testNovelTranslationBackend,
    getImageEnhanceSettings,
    updateImageEnhanceSettings,
    testImageEnhance,
    type ServerSettings,
    type ServerUser,
    type BackupSettings,
    type BackupIntervalHours,
    type TranslationCacheSettings,
    type TranslationCacheMaxBytes,
    type TranslationCacheRetentionHours,
    type TranslationTranslator,
    type MitReachabilityResult,
    type NovelTranslationSettings,
    type NovelTranslationBackend,
    type NovelTranslationReachabilityResult,
    type ImageEnhanceSettings,
    type ImageEnhanceMaxBytes,
    type ImageEnhanceRetentionHours,
    type ImageEnhanceMinShortEdge,
    type EnhanceReachabilityResult,
  } from '$lib/apis/server/api'
  import { trackOperation, operationTitle, operationToasts } from '$lib/operations.svelte'
  import * as Avatar from '$lib/components/ui/avatar/index'
  import * as AlertDialog from '$lib/components/ui/alert-dialog/index.js'
  import { Badge } from '$lib/components/ui/badge/index.js'
  import { Button, buttonVariants } from '$lib/components/ui/button/index'
  import { Input } from '$lib/components/ui/input/index.js'
  import { Label } from '$lib/components/ui/label/index.js'
  import * as Select from '$lib/components/ui/select/index.js'
  import { Switch } from '$lib/components/ui/switch/index.js'
  import { goto } from '@mateothegreat/svelte5-router'
  import ServerIcon from '@lucide/svelte/icons/server'
  import ShieldIcon from '@lucide/svelte/icons/shield'
  import TrashIcon from '@lucide/svelte/icons/trash-2'
  import UsersIcon from '@lucide/svelte/icons/users'
  import UserPlusIcon from '@lucide/svelte/icons/user-plus'
  import RefreshCwIcon from '@lucide/svelte/icons/refresh-cw'
  import CheckIcon from '@lucide/svelte/icons/check'
  import DatabaseBackupIcon from '@lucide/svelte/icons/database-backup'
  import LanguagesIcon from '@lucide/svelte/icons/languages'
  import SparklesIcon from '@lucide/svelte/icons/sparkles'
  import DownloadIcon from '@lucide/svelte/icons/download'
  import { formatServerName, serverHash } from '$lib/utils'

  let { route }: { route?: any } = $props()

  const serverUid = $derived(
    String(route?.result?.path?.params?.mediaid ?? ''),
  )

  let loading = $state(true)
  let loadError = $state<string | null>(null)
  let settings = $state<ServerSettings | null>(null)

  let nameDraft = $state('')
  let savingName = $state(false)
  let nameError = $state<string | null>(null)
  let nameSaved = $state(false)

  let roleError = $state<string | null>(null)
  let savingRoleFor = $state<string | null>(null)

  let confirmDeleteOpen = $state(false)
  let deleting = $state(false)
  let deleteError = $state<string | null>(null)

  let syncingUsers = $state(false)
  let syncUsersError = $state<string | null>(null)
  let syncUsersOk = $state<string | null>(null)

  let backup = $state<BackupSettings | null>(null)
  let backupEnabled = $state(false)
  let backupInterval = $state<BackupIntervalHours>(24)
  let savingBackup = $state(false)
  let backupSaved = $state(false)
  let backupError = $state<string | null>(null)
  let triggeringBackup = $state(false)
  let deletingBackupName = $state<string | null>(null)
  let downloadingBackupName = $state<string | null>(null)
  let trackedBackupOpId = $state<string | null>(null)

  let translationCache = $state<TranslationCacheSettings | null>(null)
  let cacheEnabled = $state(false)
  let cacheMitApiUrl = $state('http://127.0.0.1:8000')
  let cacheTranslator = $state<TranslationTranslator>('custom_openai')
  let cacheMaxBytes = $state<TranslationCacheMaxBytes>(10737418240)
  let cacheRetention = $state<TranslationCacheRetentionHours>(168)
  let cacheEligibleLanguages = $state<string[]>([...DEFAULT_TRANSLATION_ELIGIBLE_LANGUAGES])
  let savingTranslationCache = $state(false)
  let translationCacheSaved = $state(false)
  let translationCacheError = $state<string | null>(null)
  let testingMit = $state(false)
  let mitTestResult = $state<MitReachabilityResult | null>(null)

  let novelTranslation = $state<NovelTranslationSettings | null>(null)
  let novelEnabled = $state(false)
  let novelBackend = $state<NovelTranslationBackend>('ollama')
  let novelOllamaHost = $state('http://127.0.0.1:11434')
  let novelMaxBytes = $state<TranslationCacheMaxBytes>(10737418240)
  let novelRetention = $state<TranslationCacheRetentionHours>(168)
  let novelEligibleLanguages = $state<string[]>([...DEFAULT_TRANSLATION_ELIGIBLE_LANGUAGES])
  let savingNovelTranslation = $state(false)
  let novelTranslationSaved = $state(false)
  let novelTranslationError = $state<string | null>(null)
  let testingNovelBackend = $state(false)
  let novelTestResult = $state<NovelTranslationReachabilityResult | null>(null)

  let imageEnhance = $state<ImageEnhanceSettings | null>(null)
  let enhanceEnabled = $state(false)
  let enhanceApiUrl = $state('http://127.0.0.1:8090')
  let enhanceMinShortEdge = $state<ImageEnhanceMinShortEdge>(1500)
  let enhanceMaxBytes = $state<ImageEnhanceMaxBytes>(10737418240)
  let enhanceRetention = $state<ImageEnhanceRetentionHours>(168)
  let savingImageEnhance = $state(false)
  let imageEnhanceSaved = $state(false)
  let imageEnhanceError = $state<string | null>(null)
  let testingEnhance = $state(false)
  let enhanceTestResult = $state<EnhanceReachabilityResult | null>(null)

  const CACHE_SIZE_OPTIONS: { value: TranslationCacheMaxBytes; label: string }[] = [
    { value: 1073741824, label: '1 GB' },
    { value: 5368709120, label: '5 GB' },
    { value: 10737418240, label: '10 GB' },
    { value: 21474836480, label: '20 GB' },
    { value: 53687091200, label: '50 GB' },
  ]

  const CACHE_RETENTION_OPTIONS: {
    value: TranslationCacheRetentionHours
    label: string
  }[] = [
    { value: 24, label: '1 day' },
    { value: 72, label: '3 days' },
    { value: 168, label: '1 week' },
    { value: 336, label: '2 weeks' },
    { value: 720, label: '30 days' },
  ]

  const TRANSLATOR_OPTIONS: { value: TranslationTranslator; label: string }[] = [
    { value: 'deepseek', label: 'DeepSeek' },
    { value: 'custom_openai', label: 'Ollama' },
  ]

  const NOVEL_BACKEND_OPTIONS: { value: NovelTranslationBackend; label: string }[] = [
    { value: 'deepseek', label: 'DeepSeek Cloud' },
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'ollama', label: 'Ollama' },
  ]

  function toggleEligibleLanguage(list: string[], code: string): string[] {
    if (list.includes(code)) {
      const next = list.filter((c) => c !== code)
      return next.length > 0 ? next : list
    }
    return [...list, code]
  }

  const cacheSizeLabel = $derived(
    CACHE_SIZE_OPTIONS.find((o) => o.value === cacheMaxBytes)?.label ?? '10 GB',
  )

  const cacheRetentionLabel = $derived(
    CACHE_RETENTION_OPTIONS.find((o) => o.value === cacheRetention)?.label ?? '1 week',
  )

  const cacheTranslatorLabel = $derived(
    TRANSLATOR_OPTIONS.find((o) => o.value === cacheTranslator)?.label ?? 'Ollama',
  )

  const novelBackendLabel = $derived(
    NOVEL_BACKEND_OPTIONS.find((o) => o.value === novelBackend)?.label ?? 'Ollama',
  )

  const novelSizeLabel = $derived(
    CACHE_SIZE_OPTIONS.find((o) => o.value === novelMaxBytes)?.label ?? '10 GB',
  )

  const novelRetentionLabel = $derived(
    CACHE_RETENTION_OPTIONS.find((o) => o.value === novelRetention)?.label ?? '1 week',
  )

  const SHORT_EDGE_OPTIONS: { value: ImageEnhanceMinShortEdge; label: string }[] = [
    { value: 800, label: '800 px — only very small' },
    { value: 1000, label: '1000 px' },
    { value: 1200, label: '1200 px' },
    { value: 1500, label: '1500 px - recommended' },
    { value: 1800, label: '1800 px' },
    { value: 2000, label: '2000 px' },
  ]

  const enhanceShortEdgeLabel = $derived(
    SHORT_EDGE_OPTIONS.find((o) => o.value === enhanceMinShortEdge)?.label ??
      '1500 px — default (incl. Digital-1200)',
  )

  const enhanceSizeLabel = $derived(
    CACHE_SIZE_OPTIONS.find((o) => o.value === enhanceMaxBytes)?.label ?? '10 GB',
  )

  const enhanceRetentionLabel = $derived(
    CACHE_RETENTION_OPTIONS.find((o) => o.value === enhanceRetention)?.label ?? '1 week',
  )

  function sameLanguageList(a: string[], b: string[] | undefined | null): boolean {
    const aa = [...(a ?? [])].map((s) => s.toLowerCase()).sort()
    const bb = [...(b ?? [])].map((s) => s.toLowerCase()).sort()
    return aa.length === bb.length && aa.every((v, i) => v === bb[i])
  }

  /** Catppuccin mauve fill when a settings form has unsaved changes. */
  const saveDirtyClass =
    'border-latte-mauve/60 bg-latte-mauve text-white hover:bg-latte-mauve/90 dark:border-macchiato-mauve/60 dark:bg-macchiato-mauve dark:text-macchiato-base dark:hover:bg-macchiato-mauve/90'

  const nameDirty = $derived(
    nameDraft.trim() !== (gatewayServer?.name?.trim() || ''),
  )

  const backupDirty = $derived(
    backup != null &&
      (backupEnabled !== backup.enabled || backupInterval !== backup.intervalHours),
  )

  const translationCacheDirty = $derived(
    translationCache != null &&
      (cacheMitApiUrl.trim() !== (translationCache.mitApiUrl || '').trim() ||
        cacheTranslator !== translationCache.translator ||
        cacheMaxBytes !== translationCache.maxBytes ||
        cacheRetention !== translationCache.retentionHours ||
        !sameLanguageList(cacheEligibleLanguages, translationCache.eligibleLanguages)),
  )

  const novelTranslationDirty = $derived(
    novelTranslation != null &&
      (novelBackend !== novelTranslation.backend ||
        novelOllamaHost.trim() !== (novelTranslation.ollamaHost || '').trim() ||
        novelMaxBytes !== novelTranslation.maxBytes ||
        novelRetention !== novelTranslation.retentionHours ||
        !sameLanguageList(novelEligibleLanguages, novelTranslation.eligibleLanguages)),
  )

  const imageEnhanceDirty = $derived(
    imageEnhance != null &&
      (enhanceApiUrl.trim() !== (imageEnhance.enhanceApiUrl || '').trim() ||
        enhanceMinShortEdge !== imageEnhance.minShortEdge ||
        enhanceMaxBytes !== imageEnhance.maxBytes ||
        enhanceRetention !== imageEnhance.retentionHours),
  )

  const INTERVAL_OPTIONS: { value: BackupIntervalHours; label: string }[] = [
    { value: 6, label: 'Every 6 hours' },
    { value: 12, label: 'Every 12 hours' },
    { value: 24, label: 'Every day' },
    { value: 168, label: 'Every week' },
  ]

  const intervalLabel = $derived(
    INTERVAL_OPTIONS.find((o) => o.value === backupInterval)?.label ?? 'Every day',
  )

  const gatewayServer = $derived(
    (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid],
  )

  /** Deployment entry with local roles from /api/v1/auth/identity (not gateway). */
  const accessServer = $derived(
    (authState.servers ?? []).find((s) => s.uid === serverUid),
  )

  /** OWNER / ADMIN on the deployment — gateway only has OWNER vs MEMBER (non-OWNER). */
  const canManage = $derived.by(() => {
    const roles = (accessServer?.roles ?? []).map((r: string) =>
      String(r).toUpperCase(),
    )
    return roles.includes('OWNER') || roles.includes('ADMIN')
  })

  const isGatewayOwner = $derived(
    String(gatewayServer?.membership ?? '').toUpperCase() === 'OWNER',
  )

  const displayName = $derived(
    nameDraft.trim() || gatewayServer?.name?.trim() || 'Untitled server',
  )

  function roleAccent(role: string) {
    const r = role.toUpperCase()
    if (r === 'OWNER') {
      return 'border-latte-green/40 bg-latte-green/10 text-latte-green dark:border-macchiato-green/40 dark:bg-macchiato-green/10 dark:text-macchiato-green'
    }
    if (r === 'ADMIN') {
      return 'border-latte-peach/40 bg-latte-peach/10 text-latte-peach dark:border-macchiato-peach/40 dark:bg-macchiato-peach/10 dark:text-macchiato-peach'
    }
    if (r === 'MEMBER') {
      return 'border-latte-sky/40 bg-latte-sky/10 text-latte-sky dark:border-macchiato-sky/40 dark:bg-macchiato-sky/10 dark:text-macchiato-sky'
    }
    return 'border-latte-surface1/60 bg-latte-base/40 text-latte-text/70 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/40 dark:text-macchiato-text/70'
  }

  function primaryRole(user: ServerUser): string {
    const roles = (user.roles ?? []).map((r) => r.toUpperCase())
    if (roles.includes('OWNER')) return 'OWNER'
    if (roles.includes('ADMIN')) return 'ADMIN'
    if (roles.includes('MEMBER')) return 'MEMBER'
    return roles[0] ?? 'MEMBER'
  }

  function userInitials(user: ServerUser) {
    const base = user.username || user.email || '?'
    return (
      base
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('') || '?'
    )
  }

  function formatBytes(n: number) {
    if (!Number.isFinite(n) || n < 0) return '—'
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
  }

  function formatWhen(iso: string | null | undefined) {
    if (!iso) return null
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  async function loadBackup(server: any) {
    try {
      const next = await getBackupSettings(server)
      backup = next
      backupEnabled = next.enabled
      const ih = next.intervalHours as BackupIntervalHours
      backupInterval = ([6, 12, 24, 168] as BackupIntervalHours[]).includes(ih)
        ? ih
        : 24
      backupError = null
    } catch (err) {
      console.error('load backup settings failed', err)
      backupError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not load backup settings.'
    }
  }

  async function loadTranslationCache(server: any) {
    try {
      const next = await getTranslationCacheSettings(server)
      translationCache = next
      cacheEnabled = next.enabled
      cacheMitApiUrl = next.mitApiUrl || 'http://127.0.0.1:8000'
      cacheTranslator =
        next.translator === 'deepseek' || next.translator === 'custom_openai'
          ? next.translator
          : 'custom_openai'
      const mb = next.maxBytes as TranslationCacheMaxBytes
      cacheMaxBytes = CACHE_SIZE_OPTIONS.some((o) => o.value === mb) ? mb : 10737418240
      const rh = next.retentionHours as TranslationCacheRetentionHours
      cacheRetention = CACHE_RETENTION_OPTIONS.some((o) => o.value === rh) ? rh : 168
      cacheEligibleLanguages =
        Array.isArray(next.eligibleLanguages) && next.eligibleLanguages.length > 0
          ? [...next.eligibleLanguages]
          : [...DEFAULT_TRANSLATION_ELIGIBLE_LANGUAGES]
      translationCacheError = null
    } catch (err) {
      console.error('load translation cache settings failed', err)
      translationCacheError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not load translation cache settings.'
    }
  }

  async function loadNovelTranslation(server: any) {
    try {
      const next = await getNovelTranslationSettings(server)
      novelTranslation = next
      novelEnabled = next.enabled
      novelBackend =
        next.backend === 'deepseek' ||
        next.backend === 'anthropic' ||
        next.backend === 'ollama'
          ? next.backend
          : 'ollama'
      novelOllamaHost = next.ollamaHost || 'http://127.0.0.1:11434'
      const mb = next.maxBytes as TranslationCacheMaxBytes
      novelMaxBytes = CACHE_SIZE_OPTIONS.some((o) => o.value === mb) ? mb : 10737418240
      const rh = next.retentionHours as TranslationCacheRetentionHours
      novelRetention = CACHE_RETENTION_OPTIONS.some((o) => o.value === rh) ? rh : 168
      novelEligibleLanguages =
        Array.isArray(next.eligibleLanguages) && next.eligibleLanguages.length > 0
          ? [...next.eligibleLanguages]
          : [...DEFAULT_TRANSLATION_ELIGIBLE_LANGUAGES]
      novelTranslationError = null
    } catch (err) {
      console.error('load novel translation settings failed', err)
      novelTranslationError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not load novel translation settings.'
    }
  }

  async function loadImageEnhance(server: any) {
    try {
      const next = await getImageEnhanceSettings(server)
      imageEnhance = next
      enhanceEnabled = next.enabled
      enhanceApiUrl = next.enhanceApiUrl || 'http://127.0.0.1:8090'
      const edge = next.minShortEdge as ImageEnhanceMinShortEdge
      enhanceMinShortEdge = SHORT_EDGE_OPTIONS.some((o) => o.value === edge)
        ? edge
        : 1500
      const mb = next.maxBytes as ImageEnhanceMaxBytes
      enhanceMaxBytes = CACHE_SIZE_OPTIONS.some((o) => o.value === mb)
        ? (mb as ImageEnhanceMaxBytes)
        : 10737418240
      const rh = next.retentionHours as ImageEnhanceRetentionHours
      enhanceRetention = CACHE_RETENTION_OPTIONS.some((o) => o.value === rh)
        ? (rh as ImageEnhanceRetentionHours)
        : 168
      imageEnhanceError = null
    } catch (err) {
      console.error('load image enhance settings failed', err)
      imageEnhanceError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not load image enhance settings.'
    }
  }

  async function load() {
    if (!serverUid) return
    loading = true
    loadError = null
    try {
      void getIdentity()
      void refreshServerAccess()
      const listed = (await getServers())?.servers ?? []
      const server =
        listed.find((s: any) => s.uid === serverUid) ??
        (authState.servers ?? []).find((s) => s.uid === serverUid) ??
        ($serversStore as Record<string, any>)[serverUid]
      if (!server?.urls?.length) {
        loadError = 'Server not found or unreachable.'
        settings = null
        return
      }
      nameDraft = server.name?.trim() || ''
      const next = await getServerSettings(server)
      settings = next
      await loadBackup(server)
      await loadTranslationCache(server)
      await loadNovelTranslation(server)
      await loadImageEnhance(server)
    } catch (err) {
      console.error('load server settings failed', err)
      const msg = err instanceof Error ? err.message : ''
      if (msg === 'forbidden') {
        loadError = 'You need admin or owner access to manage this server.'
      } else {
        loadError = 'Could not load server settings.'
      }
      settings = null
    } finally {
      loading = false
    }
  }

  $effect(() => {
    void serverUid
    void load()
  })

  // Refresh backup list when a tracked backup operation finishes.
  $effect(() => {
    const id = trackedBackupOpId
    if (!id) return
    const toast = operationToasts.find((t) => t.id === id)
    if (!toast) return
    if (toast.status !== 'done' && toast.status !== 'failed') return
    trackedBackupOpId = null
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (server) void loadBackup(server)
  })

  async function saveBackupSchedule() {
    if (!canManage || savingBackup) return
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return
    savingBackup = true
    backupError = null
    backupSaved = false
    try {
      const next = await updateBackupSettings(server, backupEnabled, backupInterval)
      backup = next
      backupEnabled = next.enabled
      backupInterval = next.intervalHours as BackupIntervalHours
      backupSaved = true
    } catch (err) {
      console.error('save backup schedule failed', err)
      backupError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not save backup schedule.'
    } finally {
      savingBackup = false
    }
  }

  async function saveTranslationCacheSettings(opts?: {
    enabled?: boolean
  }): Promise<boolean> {
    if (!canManage || savingTranslationCache) return false
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return false
    const enabled = opts?.enabled ?? cacheEnabled
    savingTranslationCache = true
    translationCacheError = null
    translationCacheSaved = false
    try {
      const next = await updateTranslationCacheSettings(
        server,
        enabled,
        cacheMaxBytes,
        cacheRetention,
        cacheMitApiUrl.trim(),
        cacheTranslator,
        cacheEligibleLanguages,
      )
      translationCache = next
      cacheEnabled = next.enabled
      cacheMitApiUrl = next.mitApiUrl || cacheMitApiUrl
      cacheTranslator =
        next.translator === 'deepseek' || next.translator === 'custom_openai'
          ? next.translator
          : cacheTranslator
      cacheMaxBytes = next.maxBytes as TranslationCacheMaxBytes
      cacheRetention = next.retentionHours as TranslationCacheRetentionHours
      cacheEligibleLanguages =
        Array.isArray(next.eligibleLanguages) && next.eligibleLanguages.length > 0
          ? [...next.eligibleLanguages]
          : cacheEligibleLanguages
      translationCacheSaved = opts?.enabled === undefined
      return true
    } catch (err) {
      console.error('save translation cache settings failed', err)
      translationCacheError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not save translation cache settings.'
      return false
    } finally {
      savingTranslationCache = false
    }
  }

  async function setTranslationCacheEnabled(next: boolean) {
    if (!canManage || savingTranslationCache || next === cacheEnabled) return
    const prev = cacheEnabled
    cacheEnabled = next
    const ok = await saveTranslationCacheSettings({ enabled: next })
    if (!ok) cacheEnabled = prev
  }

  async function testMitConnection() {
    if (!canManage || testingMit) return
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return
    testingMit = true
    mitTestResult = null
    translationCacheError = null
    try {
      mitTestResult = await testTranslationCacheMit(
        server,
        cacheMitApiUrl.trim() || undefined,
      )
    } catch (err) {
      console.error('MIT reachability test failed', err)
      translationCacheError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not test MIT server connection.'
    } finally {
      testingMit = false
    }
  }

  async function saveNovelTranslationSettings(opts?: {
    enabled?: boolean
  }): Promise<boolean> {
    if (!canManage || savingNovelTranslation) return false
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return false
    const enabled = opts?.enabled ?? novelEnabled
    savingNovelTranslation = true
    novelTranslationError = null
    novelTranslationSaved = false
    try {
      const next = await updateNovelTranslationSettings(
        server,
        enabled,
        novelBackend,
        novelOllamaHost.trim(),
        novelMaxBytes,
        novelRetention,
        novelEligibleLanguages,
      )
      novelTranslation = next
      novelEnabled = next.enabled
      novelBackend =
        next.backend === 'deepseek' ||
        next.backend === 'anthropic' ||
        next.backend === 'ollama'
          ? next.backend
          : novelBackend
      novelOllamaHost = next.ollamaHost || novelOllamaHost
      novelMaxBytes = next.maxBytes as TranslationCacheMaxBytes
      novelRetention = next.retentionHours as TranslationCacheRetentionHours
      novelEligibleLanguages =
        Array.isArray(next.eligibleLanguages) && next.eligibleLanguages.length > 0
          ? [...next.eligibleLanguages]
          : novelEligibleLanguages
      novelTranslationSaved = opts?.enabled === undefined
      return true
    } catch (err) {
      console.error('save novel translation settings failed', err)
      novelTranslationError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not save novel translation settings.'
      return false
    } finally {
      savingNovelTranslation = false
    }
  }

  async function setNovelTranslationEnabled(next: boolean) {
    if (!canManage || savingNovelTranslation || next === novelEnabled) return
    const prev = novelEnabled
    novelEnabled = next
    const ok = await saveNovelTranslationSettings({ enabled: next })
    if (!ok) novelEnabled = prev
  }

  async function testNovelBackendConnection() {
    if (!canManage || testingNovelBackend) return
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return
    testingNovelBackend = true
    novelTestResult = null
    novelTranslationError = null
    try {
      novelTestResult = await testNovelTranslationBackend(
        server,
        novelBackend,
        novelOllamaHost.trim() || undefined,
      )
    } catch (err) {
      console.error('novel translation backend test failed', err)
      novelTranslationError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not test novel translation backend.'
    } finally {
      testingNovelBackend = false
    }
  }

  async function saveImageEnhanceSettings(opts?: {
    enabled?: boolean
  }): Promise<boolean> {
    if (!canManage || savingImageEnhance) return false
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return false
    const enabled = opts?.enabled ?? enhanceEnabled
    savingImageEnhance = true
    imageEnhanceError = null
    imageEnhanceSaved = false
    try {
      const next = await updateImageEnhanceSettings(
        server,
        enabled,
        enhanceMaxBytes,
        enhanceRetention,
        enhanceApiUrl.trim(),
        enhanceMinShortEdge,
      )
      imageEnhance = next
      enhanceEnabled = next.enabled
      enhanceApiUrl = next.enhanceApiUrl || enhanceApiUrl
      const edge = next.minShortEdge as ImageEnhanceMinShortEdge
      enhanceMinShortEdge = SHORT_EDGE_OPTIONS.some((o) => o.value === edge)
        ? edge
        : enhanceMinShortEdge
      enhanceMaxBytes = next.maxBytes as ImageEnhanceMaxBytes
      enhanceRetention = next.retentionHours as ImageEnhanceRetentionHours
      imageEnhanceSaved = opts?.enabled === undefined
      return true
    } catch (err) {
      console.error('save image enhance settings failed', err)
      imageEnhanceError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not save image enhance settings.'
      return false
    } finally {
      savingImageEnhance = false
    }
  }

  async function setImageEnhanceEnabled(next: boolean) {
    if (!canManage || savingImageEnhance || next === enhanceEnabled) return
    const prev = enhanceEnabled
    enhanceEnabled = next
    const ok = await saveImageEnhanceSettings({ enabled: next })
    if (!ok) enhanceEnabled = prev
  }

  async function testEnhanceConnection() {
    if (!canManage || testingEnhance) return
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return
    testingEnhance = true
    enhanceTestResult = null
    imageEnhanceError = null
    try {
      enhanceTestResult = await testImageEnhance(
        server,
        enhanceApiUrl.trim() || undefined,
      )
    } catch (err) {
      console.error('upscaler reachability test failed', err)
      imageEnhanceError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not test upscaler connection.'
    } finally {
      testingEnhance = false
    }
  }

  async function onBackupNow() {
    if (!canManage || triggeringBackup) return
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return
    triggeringBackup = true
    backupError = null
    try {
      const data = await triggerBackup(server)
      const op = data?.operation ?? {}
      trackedBackupOpId = op.id ?? null
      trackOperation({
        server,
        operation: op,
        title: operationTitle(op.type, 'Backing up database'),
        description: displayName,
      })
    } catch (err) {
      console.error('trigger backup failed', err)
      backupError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not start backup.'
    } finally {
      triggeringBackup = false
    }
  }

  async function onDownloadBackup(name: string) {
    if (!canManage || downloadingBackupName) return
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return
    downloadingBackupName = name
    backupError = null
    try {
      await downloadBackup(server, name)
    } catch (err) {
      console.error('download backup failed', err)
      backupError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not download backup.'
    } finally {
      downloadingBackupName = null
    }
  }

  async function onDeleteBackup(name: string) {
    if (!canManage || deletingBackupName) return
    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return
    deletingBackupName = name
    backupError = null
    try {
      await deleteBackup(server, name)
      await loadBackup(server)
    } catch (err) {
      console.error('delete backup failed', err)
      backupError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not delete backup.'
    } finally {
      deletingBackupName = null
    }
  }

  async function saveName() {
    if (!isGatewayOwner || savingName) return
    const next = nameDraft.trim()
    if (!next) {
      nameError = 'Name is required.'
      return
    }
    if (next === (gatewayServer?.name?.trim() || '')) {
      nameSaved = false
      nameError = null
      return
    }
    savingName = true
    nameError = null
    nameSaved = false
    try {
      const saved = await renameServer(serverUid, next)
      nameDraft = saved
      nameSaved = true
      await refreshServerAccess()
      const store = $serversStore as Record<string, any>
      if (store[serverUid]) {
        serversStore.set({
          ...store,
          [serverUid]: { ...store[serverUid], name: saved },
        })
      }
    } catch (err) {
      console.error('rename server failed', err)
      nameError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not save server name.'
    } finally {
      savingName = false
    }
  }

  async function onRoleChange(user: ServerUser, role: string | undefined) {
    if (!role || !settings) return
    const next = role.toUpperCase()
    if (next !== 'ADMIN' && next !== 'MEMBER') return
    if (primaryRole(user) === next) return

    const server =
      (authState.servers ?? []).find((s) => s.uid === serverUid) ??
      ($serversStore as Record<string, any>)[serverUid]
    if (!server) return

    savingRoleFor = user.userId
    roleError = null
    const prev = settings
    settings = {
      ...settings,
      users: settings.users.map((u) =>
        u.userId === user.userId ? { ...u, roles: [next] } : u,
      ),
    }
    try {
      await setServerUserRole(server, user.userId, next as 'ADMIN' | 'MEMBER')
    } catch (err) {
      console.error('set user role failed', err)
      settings = prev
      roleError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not update role.'
    } finally {
      savingRoleFor = null
    }
  }

  async function confirmDelete() {
    if (!isGatewayOwner || deleting || !serverUid) return
    deleting = true
    deleteError = null
    try {
      await deleteServer(serverUid)
      invalidateServerToken(serverUid)
      const store = $serversStore as Record<string, any>
      if (store[serverUid]) {
        const next = { ...store }
        delete next[serverUid]
        serversStore.set(next)
      }
      confirmDeleteOpen = false
      await refreshServerAccess()
      goto('/profile')
    } catch (err) {
      console.error('delete server failed', err)
      deleteError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not delete server.'
    } finally {
      deleting = false
    }
  }

  async function onSyncUsers() {
    if (!isGatewayOwner || syncingUsers || !serverUid) return
    syncingUsers = true
    syncUsersError = null
    syncUsersOk = null
    try {
      const pushed = await syncServerUsers(serverUid)
      syncUsersOk =
        pushed === 1
          ? 'Queued 1 member for sync.'
          : `Queued ${pushed} members for sync.`
    } catch (err) {
      console.error('sync users failed', err)
      syncUsersError =
        err instanceof Error && err.message
          ? err.message
          : 'Could not sync users.'
    } finally {
      syncingUsers = false
    }
  }
</script>

{#snippet featureEnableToggle(opts: {
  id: string
  checked: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
})}
  <button
    type="button"
    id={opts.id}
    role="switch"
    aria-checked={opts.checked}
    disabled={opts.disabled}
    onclick={() => {
      if (opts.disabled) return
      opts.onChange(!opts.checked)
    }}
    class="group relative inline-flex h-8 shrink-0 items-center gap-2 rounded-full border px-1.5
      py-1 transition-all duration-300 ease-out
      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-latte-mauve/40
      disabled:cursor-not-allowed disabled:opacity-50
      dark:focus-visible:ring-macchiato-mauve/40
      {opts.checked
        ? 'border-latte-green/45 bg-gradient-to-r from-latte-green/15 to-latte-sky/10 shadow-[inset_0_0_0_1px_rgba(64,160,43,0.12)] dark:border-macchiato-green/45 dark:from-macchiato-green/20 dark:to-macchiato-sky/10 dark:shadow-[inset_0_0_0_1px_rgba(166,218,149,0.12)]'
        : 'border-latte-surface1/70 bg-latte-base/55 dark:border-macchiato-surface1/70 dark:bg-macchiato-base/55'}"
  >
    <span
      class="pl-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors
        duration-300
        {opts.checked
          ? 'text-latte-green dark:text-macchiato-green'
          : 'text-latte-text/40 dark:text-macchiato-text/40'}"
    >
      {opts.checked ? 'On' : 'Off'}
    </span>
    <span
      class="relative flex h-5 w-9 items-center rounded-full transition-colors duration-300
        {opts.checked
          ? 'bg-latte-green dark:bg-macchiato-green'
          : 'bg-latte-surface1 dark:bg-macchiato-surface1'}"
      aria-hidden="true"
    >
      <span
        class="absolute top-0.5 left-0.5 size-4 rounded-full bg-latte-base shadow-sm
          transition-transform duration-300 ease-out dark:bg-macchiato-base
          {opts.checked ? 'translate-x-4' : 'translate-x-0'}"
      ></span>
    </span>
  </button>
{/snippet}

<div class="mx-4 mt-20 mb-10 max-w-xl space-y-4 sm:mx-10 md:mx-auto">
  <div
    class="flex min-w-0 flex-col gap-0.5 rounded-md border border-latte-surface1/60
      bg-latte-mantle/85 px-3 py-1.5 shadow-sm backdrop-blur-sm
      dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/85"
  >
    <span class="text-[8px] leading-none font-light text-latte-peach dark:text-macchiato-peach">
      server
    </span>
    <h1
      class="truncate text-lg font-semibold leading-tight text-latte-text
        dark:text-macchiato-text sm:text-xl"
    >
      Settings
    </h1>
  </div>

  {#if loading && !settings}
    <section
      class="rounded-lg border border-latte-surface1/60 bg-latte-mantle/70 px-5 py-8
        text-sm text-latte-text/60 shadow-sm backdrop-blur-sm
        dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70 dark:text-macchiato-text/60"
    >
      Loading server settings…
    </section>
  {:else if loadError}
    <section
      class="rounded-lg border border-latte-surface1/60 bg-latte-mantle/70 px-5 py-6
        shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
    >
      <p class="text-sm text-latte-red dark:text-macchiato-red">{loadError}</p>
      <Button
        class="mt-4"
        variant="outline"
        size="sm"
        onclick={() => goto('/profile')}
      >
        Back to profile
      </Button>
    </section>
  {:else if settings}
    <section
      class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
        shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
    >
      <div
        class="flex items-center gap-2 border-b border-latte-surface1/50 px-5 py-3.5
          dark:border-macchiato-surface1/50"
      >
        <ServerIcon
          class="size-4 shrink-0 text-latte-peach dark:text-macchiato-peach"
          strokeWidth={1.75}
        />
        <div class="min-w-0">
          <p
            class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
              dark:text-macchiato-text/55"
          >
            Identity
          </p>
          <p class="truncate text-sm font-semibold text-latte-text dark:text-macchiato-text">
            {displayName}
            <span class="font-normal text-latte-text/50 dark:text-macchiato-text/50">
              #{serverHash(serverUid)}
            </span>
          </p>
        </div>
      </div>

      <div class="space-y-4 px-5 py-4">
        <div class="flex flex-col gap-2">
          <Label
            for="server-name"
            class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
              dark:text-macchiato-text/55"
          >
            Server name
          </Label>
          <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              id="server-name"
              bind:value={nameDraft}
              disabled={!isGatewayOwner || savingName}
              placeholder="Server name…"
              autocomplete="off"
              class="flex-1"
              onkeydown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void saveName()
                }
              }}
            />
            {#if isGatewayOwner}
              <Button
                size="sm"
                variant="outline"
                class="shrink-0 gap-1.5 {nameDirty ? saveDirtyClass : ''}"
                disabled={savingName || !nameDraft.trim() || !nameDirty}
                onclick={() => void saveName()}
              >
                {#if nameSaved && !savingName && !nameDirty}
                  <CheckIcon class="size-3.5" strokeWidth={2} />
                  Saved
                {:else}
                  Save
                {/if}
              </Button>
            {/if}
          </div>
          {#if !isGatewayOwner}
            <p class="text-xs text-latte-text/50 dark:text-macchiato-text/50">
              Only the gateway owner can rename this server.
            </p>
          {/if}
          {#if nameError}
            <p class="text-sm text-latte-red dark:text-macchiato-red">{nameError}</p>
          {/if}
        </div>

        <div class="flex items-start gap-3 border-t border-latte-surface1/50 pt-4 dark:border-macchiato-surface1/50">
          <ShieldIcon
            class="mt-0.5 size-4 shrink-0 text-latte-green dark:text-macchiato-green"
            strokeWidth={1.75}
          />
          <div class="min-w-0 flex flex-col gap-1">
            <p
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Claim status
            </p>
            <div class="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                class="rounded-md font-medium capitalize {settings.claimed
                  ? roleAccent('OWNER')
                  : 'border-latte-surface1/60 bg-latte-base/40 text-latte-text/70 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/40 dark:text-macchiato-text/70'}"
              >
                {settings.claimed ? 'claimed' : 'unclaimed'}
              </Badge>
              {#if gatewayServer?.ownerName}
                <span class="text-sm text-latte-text/70 dark:text-macchiato-text/70">
                  owner {gatewayServer.ownerName}{#if gatewayServer.ownerHash}<span
                      class="opacity-70">#{gatewayServer.ownerHash}</span
                    >{/if}
                </span>
              {/if}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
        shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
    >
      <div
        class="flex items-center justify-between gap-3 border-b border-latte-surface1/50 px-5 py-3.5
          dark:border-macchiato-surface1/50"
      >
        <div class="flex min-w-0 items-center gap-2">
          <UsersIcon
            class="size-4 shrink-0 text-latte-sky dark:text-macchiato-sky"
            strokeWidth={1.75}
          />
          <div class="min-w-0">
            <p
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Users
            </p>
            <p class="text-sm font-semibold text-latte-text dark:text-macchiato-text">
              {settings.users.length} user{settings.users.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        {#if isGatewayOwner}
          <div class="flex shrink-0 items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              class="gap-1.5"
              disabled={syncingUsers}
              onclick={() => void onSyncUsers()}
            >
              <RefreshCwIcon
                class="size-4 {syncingUsers ? 'animate-spin' : ''}"
                strokeWidth={1.75}
              />
              <span class="hidden sm:inline">
                {syncingUsers ? 'Syncing…' : 'Sync users'}
              </span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              class="gap-1.5"
              onclick={() =>
                openGrantAccess(serverUid, gatewayServer?.name ?? nameDraft)}
            >
              <UserPlusIcon class="size-4" strokeWidth={1.75} />
              <span class="hidden sm:inline">Invite</span>
            </Button>
          </div>
        {/if}
      </div>

      {#if syncUsersError}
        <p class="px-5 pt-4 text-sm text-latte-red dark:text-macchiato-red">{syncUsersError}</p>
      {:else if syncUsersOk}
        <p class="px-5 pt-4 text-sm text-latte-green dark:text-macchiato-green">{syncUsersOk}</p>
      {/if}

      {#if roleError}
        <p class="px-5 pt-4 text-sm text-latte-red dark:text-macchiato-red">{roleError}</p>
      {/if}

      {#if settings.users.length === 0}
        <p class="px-5 py-6 text-sm text-latte-text/60 dark:text-macchiato-text/60">
          No users on this deployment yet.
        </p>
      {:else}
        <ul class="divide-y divide-latte-surface1/50 dark:divide-macchiato-surface1/50">
          {#each settings.users as user (user.userId)}
            {@const role = primaryRole(user)}
            {@const isOwner = role === 'OWNER'}
            <li class="flex items-center gap-3 px-5 py-3.5">
              <Avatar.Root
                class="h-9 w-9 shrink-0 ring-1 ring-latte-surface1/70
                  dark:ring-macchiato-surface1/70"
              >
                <Avatar.Fallback
                  class="bg-latte-base text-xs font-semibold text-latte-text
                    dark:bg-macchiato-base dark:text-macchiato-text"
                >
                  {userInitials(user)}
                </Avatar.Fallback>
              </Avatar.Root>
              <div class="min-w-0 flex flex-1 flex-col gap-0.5">
                <span
                  class="truncate text-sm font-medium text-latte-text dark:text-macchiato-text"
                >
                  {user.username || 'Unknown'}
                </span>
                <span
                  class="truncate text-[10px] text-latte-text/45 dark:text-macchiato-text/45"
                >
                  {user.email || user.userId}
                </span>
              </div>
              {#if isOwner || !canManage}
                <Badge
                  variant="outline"
                  class="shrink-0 rounded-md font-medium capitalize {roleAccent(role)}"
                >
                  {role.toLowerCase()}
                </Badge>
              {:else}
                <Select.Root
                  type="single"
                  value={role}
                  onValueChange={(v) => void onRoleChange(user, v)}
                  disabled={savingRoleFor === user.userId}
                >
                  <Select.Trigger
                    class="h-8 w-[7.5rem] shrink-0 capitalize {roleAccent(role)}"
                    aria-label={`Role for ${user.username || user.email}`}
                  >
                    {role.toLowerCase()}
                  </Select.Trigger>
                  <Select.Content class="z-62">
                    <Select.Item value="ADMIN" label="admin">admin</Select.Item>
                    <Select.Item value="MEMBER" label="member">member</Select.Item>
                  </Select.Content>
                </Select.Root>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </section>

    {#if canManage}
      <section
        class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
          shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
      >
        <div
          class="flex items-center justify-between gap-3 border-b border-latte-surface1/50 px-5 py-3.5
            dark:border-macchiato-surface1/50"
        >
          <div class="flex min-w-0 items-center gap-2">
            <DatabaseBackupIcon
              class="size-4 shrink-0 text-latte-mauve dark:text-macchiato-mauve"
              strokeWidth={1.75}
            />
            <div class="min-w-0">
              <p
                class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                  dark:text-macchiato-text/55"
              >
                Backup
              </p>
              <p class="text-sm font-semibold text-latte-text dark:text-macchiato-text">
                Database backups
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            class="shrink-0 gap-1.5"
            disabled={triggeringBackup}
            onclick={() => void onBackupNow()}
          >
            <DatabaseBackupIcon
              class="size-4 {triggeringBackup ? 'animate-pulse' : ''}"
              strokeWidth={1.75}
            />
            <span class="hidden sm:inline">
              {triggeringBackup ? 'Queuing…' : 'Backup now'}
            </span>
          </Button>
        </div>

        <div class="space-y-4 px-5 py-4">
          <div class="flex items-center justify-between gap-3">
            <div class="min-w-0">
              <Label
                for="backup-enabled"
                class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                  dark:text-macchiato-text/55"
              >
                Scheduled backups
              </Label>
              <p class="text-sm text-latte-text/70 dark:text-macchiato-text/70">
                Automatically snapshot the database on a schedule
              </p>
            </div>
            <Switch
              id="backup-enabled"
              checked={backupEnabled}
              onCheckedChange={(v: boolean) => {
                backupEnabled = v
                backupSaved = false
              }}
              disabled={savingBackup}
            />
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="backup-interval"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Interval
            </Label>
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Select.Root
                type="single"
                value={String(backupInterval)}
                onValueChange={(v) => {
                  const n = Number(v)
                  if (n === 6 || n === 12 || n === 24 || n === 168) {
                    backupInterval = n
                    backupSaved = false
                  }
                }}
                disabled={savingBackup || !backupEnabled}
              >
                <Select.Trigger
                  id="backup-interval"
                  class="h-9 w-full sm:w-[14rem]"
                  aria-label="Backup interval"
                >
                  {intervalLabel}
                </Select.Trigger>
                <Select.Content class="z-62">
                  {#each INTERVAL_OPTIONS as opt (opt.value)}
                    <Select.Item value={String(opt.value)} label={opt.label}>
                      {opt.label}
                    </Select.Item>
                  {/each}
                </Select.Content>
              </Select.Root>
              <Button
                size="sm"
                variant="outline"
                class="shrink-0 gap-1.5 {backupDirty ? saveDirtyClass : ''}"
                disabled={savingBackup || !backupDirty}
                onclick={() => void saveBackupSchedule()}
              >
                {#if backupSaved && !savingBackup && !backupDirty}
                  <CheckIcon class="size-3.5" strokeWidth={2} />
                  Saved
                {:else}
                  Save schedule
                {/if}
              </Button>
            </div>
          </div>

          {#if backup?.lastSuccessAt || backup?.lastError}
            <div
              class="space-y-1 border-t border-latte-surface1/50 pt-3
                dark:border-macchiato-surface1/50"
            >
              {#if formatWhen(backup?.lastSuccessAt)}
                <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
                  Last success: {formatWhen(backup?.lastSuccessAt)}
                </p>
              {/if}
              {#if backup?.lastError}
                <p class="text-xs text-latte-red dark:text-macchiato-red">
                  Last error: {backup.lastError}
                </p>
              {/if}
            </div>
          {/if}

          {#if backupError}
            <p class="text-sm text-latte-red dark:text-macchiato-red">{backupError}</p>
          {/if}

          <div
            class="border-t border-latte-surface1/50 pt-3 dark:border-macchiato-surface1/50"
          >
            <p
              class="mb-2 text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Available backups
            </p>
            {#if !backup?.backups?.length}
              <p class="text-sm text-latte-text/60 dark:text-macchiato-text/60">
                No backups yet.
              </p>
            {:else}
              <ul class="divide-y divide-latte-surface1/50 dark:divide-macchiato-surface1/50">
                {#each backup.backups as file (file.name)}
                  <li class="flex items-center gap-2 py-2.5">
                    <div class="min-w-0 flex-1">
                      <p
                        class="truncate text-sm font-medium text-latte-text
                          dark:text-macchiato-text"
                      >
                        {file.name}
                      </p>
                      <p class="text-[10px] text-latte-text/45 dark:text-macchiato-text/45">
                        {formatBytes(file.size)}
                        {#if formatWhen(file.createdAt)}
                          · {formatWhen(file.createdAt)}
                        {/if}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      class="size-8 shrink-0 p-0"
                      disabled={downloadingBackupName === file.name}
                      aria-label={`Download ${file.name}`}
                      onclick={() => void onDownloadBackup(file.name)}
                    >
                      <DownloadIcon class="size-4" strokeWidth={1.75} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      class="size-8 shrink-0 p-0 text-latte-red dark:text-macchiato-red"
                      disabled={deletingBackupName === file.name}
                      aria-label={`Delete ${file.name}`}
                      onclick={() => void onDeleteBackup(file.name)}
                    >
                      <TrashIcon class="size-4" strokeWidth={1.75} />
                    </Button>
                  </li>
                {/each}
              </ul>
            {/if}
          </div>
        </div>
      </section>

      <section
        class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
          shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
      >
        <div
          class="flex items-center justify-between gap-3 border-b border-latte-surface1/50 px-5 py-3.5
            dark:border-macchiato-surface1/50"
        >
          <div class="flex min-w-0 items-center gap-2">
            <LanguagesIcon
              class="size-4 shrink-0 text-latte-mauve dark:text-macchiato-mauve"
              strokeWidth={1.75}
            />
            <div class="min-w-0">
              <p
                class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                  dark:text-macchiato-text/55"
              >
                Translation
              </p>
              <p class="text-sm font-semibold text-latte-text dark:text-macchiato-text">
                Manga and webtoon translation
              </p>
            </div>
          </div>
          {@render featureEnableToggle({
            id: 'cache-enabled',
            checked: cacheEnabled,
            disabled: savingTranslationCache,
            onChange: (v) => {
              void setTranslationCacheEnabled(v)
            },
          })}
        </div>

        <div class="space-y-4 px-5 py-4">
          <p class="text-sm text-latte-text/70 dark:text-macchiato-text/70">
            Allow translated manga and webtoon pages via the image translator.
          </p>

          <div class="flex flex-col gap-2">
            <Label
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Eligible source languages
            </Label>
            <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
              Translation is offered only for libraries whose language is selected here.
            </p>
            <div class="flex flex-wrap gap-1.5">
              {#each TRANSLATION_SOURCE_LANGUAGES as opt (opt.value)}
                {@const active = cacheEligibleLanguages.includes(opt.value)}
                <button
                  type="button"
                  disabled={savingTranslationCache ||
                    (active && cacheEligibleLanguages.length === 1)}
                  class="rounded-md border px-2.5 py-1 text-xs transition-colors
                    {active
                      ? 'border-latte-mauve/50 bg-latte-mauve/15 text-latte-mauve dark:border-macchiato-mauve/50 dark:bg-macchiato-mauve/15 dark:text-macchiato-mauve'
                      : 'border-latte-surface1/70 text-latte-text/70 hover:border-latte-surface1 dark:border-macchiato-surface1/70 dark:text-macchiato-text/70'}
                    disabled:opacity-50"
                  onclick={() => {
                    cacheEligibleLanguages = toggleEligibleLanguage(
                      cacheEligibleLanguages,
                      opt.value,
                    )
                    translationCacheSaved = false
                  }}
                >
                  {opt.label}
                </button>
              {/each}
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="cache-mit-url"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              MIT server URL
            </Label>
            <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
              Base URL of the manga-image-translator API.
            </p>
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                id="cache-mit-url"
                bind:value={cacheMitApiUrl}
                disabled={savingTranslationCache}
                placeholder="http://127.0.0.1:8000"
                autocomplete="off"
                class="flex-1 font-mono text-sm"
                oninput={() => {
                  translationCacheSaved = false
                  mitTestResult = null
                }}
              />
              <Button
                size="sm"
                variant="outline"
                class="shrink-0 gap-1.5"
                disabled={savingTranslationCache || testingMit || !cacheMitApiUrl.trim()}
                onclick={() => void testMitConnection()}
              >
                <RefreshCwIcon
                  class="size-3.5 {testingMit ? 'animate-spin' : ''}"
                  strokeWidth={2}
                />
                {testingMit ? 'Testing…' : 'Test connection'}
              </Button>
            </div>
            {#if mitTestResult}
              <p
                class="text-xs {mitTestResult.ok
                  ? 'text-latte-green dark:text-macchiato-green'
                  : 'text-latte-red dark:text-macchiato-red'}"
              >
                {mitTestResult.message}
                {#if mitTestResult.latencyMs != null}
                  · {mitTestResult.latencyMs} ms
                {/if}
              </p>
            {/if}
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="cache-translator"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Translator
            </Label>
            <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
              Backend used by manga-image-translator for text translation.
            </p>
            <Select.Root
              type="single"
              value={cacheTranslator}
              onValueChange={(v) => {
                if (v === 'deepseek' || v === 'custom_openai') {
                  cacheTranslator = v
                  translationCacheSaved = false
                }
              }}
              disabled={savingTranslationCache}
            >
              <Select.Trigger id="cache-translator" class="h-9 w-full sm:w-[14rem]">
                {cacheTranslatorLabel}
              </Select.Trigger>
              <Select.Content class="z-62">
                {#each TRANSLATOR_OPTIONS as opt (opt.value)}
                  <Select.Item value={opt.value} label={opt.label}>
                    {opt.label}
                  </Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          </div>

          <div class="space-y-1">
            <p class="text-sm text-latte-text/70 dark:text-macchiato-text/70">
              Cached translated pages for manga and webtoon libraries.
            </p>
            {#if translationCache}
              <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
                Using {formatBytes(translationCache.usedBytes)} of {formatBytes(translationCache.maxBytes)}
                · {translationCache.entryCount} pages
              </p>
            {/if}
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="cache-max-bytes"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Max cache size
            </Label>
            <Select.Root
              type="single"
              value={String(cacheMaxBytes)}
              onValueChange={(v) => {
                const n = Number(v)
                if (CACHE_SIZE_OPTIONS.some((o) => o.value === n)) {
                  cacheMaxBytes = n as TranslationCacheMaxBytes
                  translationCacheSaved = false
                }
              }}
              disabled={savingTranslationCache || !cacheEnabled}
            >
              <Select.Trigger id="cache-max-bytes" class="h-9 w-full sm:w-[14rem]">
                {cacheSizeLabel}
              </Select.Trigger>
              <Select.Content class="z-62">
                {#each CACHE_SIZE_OPTIONS as opt (opt.value)}
                  <Select.Item value={String(opt.value)} label={opt.label}>
                    {opt.label}
                  </Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="cache-retention"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Retention
            </Label>
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Select.Root
                type="single"
                value={String(cacheRetention)}
                onValueChange={(v) => {
                  const n = Number(v)
                  if (CACHE_RETENTION_OPTIONS.some((o) => o.value === n)) {
                    cacheRetention = n as TranslationCacheRetentionHours
                    translationCacheSaved = false
                  }
                }}
                disabled={savingTranslationCache || !cacheEnabled}
              >
                <Select.Trigger id="cache-retention" class="h-9 w-full sm:w-[14rem]">
                  {cacheRetentionLabel}
                </Select.Trigger>
                <Select.Content class="z-62">
                  {#each CACHE_RETENTION_OPTIONS as opt (opt.value)}
                    <Select.Item value={String(opt.value)} label={opt.label}>
                      {opt.label}
                    </Select.Item>
                  {/each}
                </Select.Content>
              </Select.Root>
              <Button
                size="sm"
                variant="outline"
                class="shrink-0 gap-1.5 {translationCacheDirty ? saveDirtyClass : ''}"
                disabled={savingTranslationCache || !translationCacheDirty}
                onclick={() => void saveTranslationCacheSettings()}
              >
                {#if translationCacheSaved && !savingTranslationCache && !translationCacheDirty}
                  <CheckIcon class="size-3.5" strokeWidth={2} />
                  Saved
                {:else}
                  Save cache settings
                {/if}
              </Button>
            </div>
          </div>

          {#if translationCache?.lastPruneAt || translationCache?.lastPruneError}
            <div
              class="space-y-1 border-t border-latte-surface1/50 pt-3
                dark:border-macchiato-surface1/50"
            >
              {#if formatWhen(translationCache?.lastPruneAt)}
                <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
                  Last prune: {formatWhen(translationCache?.lastPruneAt)}
                </p>
              {/if}
              {#if translationCache?.lastPruneError}
                <p class="text-xs text-latte-red dark:text-macchiato-red">
                  Last prune error: {translationCache.lastPruneError}
                </p>
              {/if}
            </div>
          {/if}

          {#if translationCacheError}
            <p class="text-sm text-latte-red dark:text-macchiato-red">{translationCacheError}</p>
          {/if}
        </div>
      </section>

      <section
        class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
          shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
      >
        <div
          class="flex items-center justify-between gap-3 border-b border-latte-surface1/50 px-5
            py-3.5 dark:border-macchiato-surface1/50"
        >
          <div class="flex min-w-0 items-center gap-2">
            <LanguagesIcon
              class="size-4 shrink-0 text-latte-mauve dark:text-macchiato-mauve"
              strokeWidth={1.75}
            />
            <div class="min-w-0">
              <p
                class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                  dark:text-macchiato-text/55"
              >
                Translation
              </p>
              <p class="text-sm font-semibold text-latte-text dark:text-macchiato-text">
                Novel and webnovel translation
              </p>
            </div>
          </div>
          {@render featureEnableToggle({
            id: 'novel-tl-enabled',
            checked: novelEnabled,
            disabled: savingNovelTranslation,
            onChange: (v) => {
              void setNovelTranslationEnabled(v)
            },
          })}
        </div>

        <div class="space-y-4 px-5 py-4">
          <p class="text-sm text-latte-text/70 dark:text-macchiato-text/70">
            Allow translated novel and webnovel pages via LLM backends.
          </p>

          <div class="flex flex-col gap-2">
            <Label
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Eligible source languages
            </Label>
            <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
              Translation is offered only for libraries whose language is selected here.
            </p>
            <div class="flex flex-wrap gap-1.5">
              {#each TRANSLATION_SOURCE_LANGUAGES as opt (opt.value)}
                {@const active = novelEligibleLanguages.includes(opt.value)}
                <button
                  type="button"
                  disabled={savingNovelTranslation ||
                    (active && novelEligibleLanguages.length === 1)}
                  class="rounded-md border px-2.5 py-1 text-xs transition-colors
                    {active
                      ? 'border-latte-mauve/50 bg-latte-mauve/15 text-latte-mauve dark:border-macchiato-mauve/50 dark:bg-macchiato-mauve/15 dark:text-macchiato-mauve'
                      : 'border-latte-surface1/70 text-latte-text/70 hover:border-latte-surface1 dark:border-macchiato-surface1/70 dark:text-macchiato-text/70'}
                    disabled:opacity-50"
                  onclick={() => {
                    novelEligibleLanguages = toggleEligibleLanguage(
                      novelEligibleLanguages,
                      opt.value,
                    )
                    novelTranslationSaved = false
                  }}
                >
                  {opt.label}
                </button>
              {/each}
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="novel-tl-backend"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Backend
            </Label>
            <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
              API tokens for DeepSeek and Anthropic are read from server environment
              variables (DEEPSEEK_API_KEY, ANTHROPIC_API_KEY) — not stored in the
              database.
            </p>
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Select.Root
                type="single"
                value={novelBackend}
                onValueChange={(v) => {
                  if (v === 'deepseek' || v === 'anthropic' || v === 'ollama') {
                    novelBackend = v
                    novelTranslationSaved = false
                    novelTestResult = null
                  }
                }}
                disabled={savingNovelTranslation}
              >
                <Select.Trigger id="novel-tl-backend" class="h-9 w-full sm:w-[14rem]">
                  {novelBackendLabel}
                </Select.Trigger>
                <Select.Content>
                  {#each NOVEL_BACKEND_OPTIONS as opt}
                    <Select.Item value={opt.value}>{opt.label}</Select.Item>
                  {/each}
                </Select.Content>
              </Select.Root>
              <Button
                size="sm"
                variant="outline"
                class="shrink-0 gap-1.5"
                disabled={savingNovelTranslation || testingNovelBackend}
                onclick={() => void testNovelBackendConnection()}
              >
                <RefreshCwIcon
                  class="size-3.5 {testingNovelBackend ? 'animate-spin' : ''}"
                  strokeWidth={2}
                />
                {testingNovelBackend ? 'Testing…' : 'Test connection'}
              </Button>
            </div>
            {#if novelTestResult}
              <p
                class="text-xs {novelTestResult.ok
                  ? 'text-latte-green dark:text-macchiato-green'
                  : 'text-latte-red dark:text-macchiato-red'}"
              >
                {novelTestResult.message}
                {#if novelTestResult.latencyMs != null}
                  · {novelTestResult.latencyMs} ms
                {/if}
              </p>
            {/if}
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="novel-tl-ollama-host"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Ollama host
            </Label>
            <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
              Base URL of the Ollama API (used when backend is Ollama).
            </p>
            <Input
              id="novel-tl-ollama-host"
              bind:value={novelOllamaHost}
              disabled={savingNovelTranslation || novelBackend !== 'ollama'}
              placeholder="http://127.0.0.1:11434"
              autocomplete="off"
              class="font-mono text-sm"
              oninput={() => {
                novelTranslationSaved = false
                novelTestResult = null
              }}
            />
          </div>

          <div class="space-y-1">
            <p class="text-sm text-latte-text/70 dark:text-macchiato-text/70">
              Cached translated pages for novel and webnovel libraries.
            </p>
            {#if novelTranslation}
              <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
                Using {formatBytes(novelTranslation.usedBytes)} of {formatBytes(novelTranslation.maxBytes)}
                · {novelTranslation.entryCount} pages
              </p>
            {/if}
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="novel-tl-max"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Max cache size
            </Label>
            <Select.Root
              type="single"
              value={String(novelMaxBytes)}
              onValueChange={(v) => {
                const n = Number(v)
                if (CACHE_SIZE_OPTIONS.some((o) => o.value === n)) {
                  novelMaxBytes = n as TranslationCacheMaxBytes
                  novelTranslationSaved = false
                }
              }}
              disabled={savingNovelTranslation || !novelEnabled}
            >
              <Select.Trigger id="novel-tl-max" class="h-9 w-full sm:w-[14rem]">
                {novelSizeLabel}
              </Select.Trigger>
              <Select.Content class="z-62">
                {#each CACHE_SIZE_OPTIONS as opt (opt.value)}
                  <Select.Item value={String(opt.value)} label={opt.label}>
                    {opt.label}
                  </Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="novel-tl-retention"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Retention
            </Label>
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Select.Root
                type="single"
                value={String(novelRetention)}
                onValueChange={(v) => {
                  const n = Number(v)
                  if (CACHE_RETENTION_OPTIONS.some((o) => o.value === n)) {
                    novelRetention = n as TranslationCacheRetentionHours
                    novelTranslationSaved = false
                  }
                }}
                disabled={savingNovelTranslation || !novelEnabled}
              >
                <Select.Trigger id="novel-tl-retention" class="h-9 w-full sm:w-[14rem]">
                  {novelRetentionLabel}
                </Select.Trigger>
                <Select.Content class="z-62">
                  {#each CACHE_RETENTION_OPTIONS as opt (opt.value)}
                    <Select.Item value={String(opt.value)} label={opt.label}>
                      {opt.label}
                    </Select.Item>
                  {/each}
                </Select.Content>
              </Select.Root>
              <Button
                size="sm"
                variant="outline"
                class="shrink-0 gap-1.5 {novelTranslationDirty ? saveDirtyClass : ''}"
                disabled={savingNovelTranslation || !novelTranslationDirty}
                onclick={() => void saveNovelTranslationSettings()}
              >
                {#if novelTranslationSaved && !savingNovelTranslation && !novelTranslationDirty}
                  <CheckIcon class="size-3.5" strokeWidth={2} />
                  Saved
                {:else}
                  Save novel settings
                {/if}
              </Button>
            </div>
          </div>

          {#if novelTranslation?.lastPruneAt || novelTranslation?.lastPruneError}
            <div
              class="space-y-1 border-t border-latte-surface1/50 pt-3
                dark:border-macchiato-surface1/50"
            >
              {#if formatWhen(novelTranslation?.lastPruneAt)}
                <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
                  Last prune: {formatWhen(novelTranslation?.lastPruneAt)}
                </p>
              {/if}
              {#if novelTranslation?.lastPruneError}
                <p class="text-xs text-latte-red dark:text-macchiato-red">
                  Last prune error: {novelTranslation.lastPruneError}
                </p>
              {/if}
            </div>
          {/if}

          {#if novelTranslationError}
            <p class="text-sm text-latte-red dark:text-macchiato-red">{novelTranslationError}</p>
          {/if}
        </div>
      </section>

      <section
        class="overflow-hidden rounded-lg border border-latte-surface1/60 bg-latte-mantle/70
          shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/70"
      >
        <div
          class="flex items-center justify-between gap-3 border-b border-latte-surface1/50 px-5
            py-3.5 dark:border-macchiato-surface1/50"
        >
          <div class="flex min-w-0 items-center gap-2">
            <SparklesIcon
              class="size-4 shrink-0 text-latte-mauve dark:text-macchiato-mauve"
              strokeWidth={1.75}
            />
            <div class="min-w-0">
              <p
                class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                  dark:text-macchiato-text/55"
              >
                Image quality
              </p>
              <p class="text-sm font-semibold text-latte-text dark:text-macchiato-text">
                Manga and webtoon page enhancement
              </p>
            </div>
          </div>
          {@render featureEnableToggle({
            id: 'enhance-enabled',
            checked: enhanceEnabled,
            disabled: savingImageEnhance,
            onChange: (v) => {
              void setImageEnhanceEnabled(v)
            },
          })}
        </div>

        <div class="space-y-4 px-5 py-4">
          <p class="text-sm text-latte-text/70 dark:text-macchiato-text/70">
            Upscale heavily compressed manga and webtoon pages when the upscaler is
            deployed.
          </p>

          <div class="flex flex-col gap-2">
            <Label
              for="enhance-api-url"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Upscaler URL
            </Label>
            <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
              Base URL of the nuread-upscaler service.
            </p>
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                id="enhance-api-url"
                bind:value={enhanceApiUrl}
                disabled={savingImageEnhance}
                placeholder="http://127.0.0.1:8090"
                autocomplete="off"
                class="flex-1 font-mono text-sm"
                oninput={() => {
                  imageEnhanceSaved = false
                  enhanceTestResult = null
                }}
              />
              <Button
                size="sm"
                variant="outline"
                class="shrink-0 gap-1.5"
                disabled={savingImageEnhance || testingEnhance || !enhanceApiUrl.trim()}
                onclick={() => void testEnhanceConnection()}
              >
                <RefreshCwIcon
                  class="size-3.5 {testingEnhance ? 'animate-spin' : ''}"
                  strokeWidth={2}
                />
                {testingEnhance ? 'Testing…' : 'Test connection'}
              </Button>
            </div>
            {#if enhanceTestResult}
              <p
                class="text-xs {enhanceTestResult.ok
                  ? 'text-latte-green dark:text-macchiato-green'
                  : 'text-latte-red dark:text-macchiato-red'}"
              >
                {enhanceTestResult.message}
                {#if enhanceTestResult.latencyMs != null}
                  · {enhanceTestResult.latencyMs} ms
                {/if}
              </p>
            {/if}
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="enhance-min-short-edge"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Min short-edge resolution
            </Label>
            <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
              Enhance when the shorter side is strictly below this size,
              higher values enhance more pages.
            </p>
            <Select.Root
              type="single"
              value={String(enhanceMinShortEdge)}
              onValueChange={(v) => {
                const n = Number(v) as ImageEnhanceMinShortEdge
                if (SHORT_EDGE_OPTIONS.some((o) => o.value === n)) {
                  enhanceMinShortEdge = n
                  imageEnhanceSaved = false
                }
              }}
              disabled={savingImageEnhance || !enhanceEnabled}
            >
              <Select.Trigger id="enhance-min-short-edge" class="h-9 w-full sm:w-[18rem]">
                {enhanceShortEdgeLabel}
              </Select.Trigger>
              <Select.Content class="z-62">
                {#each SHORT_EDGE_OPTIONS as opt (opt.value)}
                  <Select.Item value={String(opt.value)} label={opt.label}>
                    {opt.label}
                  </Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          </div>

          <div class="space-y-1">
            <p class="text-sm text-latte-text/70 dark:text-macchiato-text/70">
              Cached enhanced pages for manga and webtoon libraries.
            </p>
            {#if imageEnhance}
              <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
                Using {formatBytes(imageEnhance.usedBytes)} of {formatBytes(imageEnhance.maxBytes)}
                · {imageEnhance.entryCount} pages
              </p>
            {/if}
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="enhance-max-bytes"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Max cache size
            </Label>
            <Select.Root
              type="single"
              value={String(enhanceMaxBytes)}
              onValueChange={(v) => {
                const n = Number(v)
                if (CACHE_SIZE_OPTIONS.some((o) => o.value === n)) {
                  enhanceMaxBytes = n as ImageEnhanceMaxBytes
                  imageEnhanceSaved = false
                }
              }}
              disabled={savingImageEnhance || !enhanceEnabled}
            >
              <Select.Trigger id="enhance-max-bytes" class="h-9 w-full sm:w-[14rem]">
                {enhanceSizeLabel}
              </Select.Trigger>
              <Select.Content class="z-62">
                {#each CACHE_SIZE_OPTIONS as opt (opt.value)}
                  <Select.Item value={String(opt.value)} label={opt.label}>
                    {opt.label}
                  </Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          </div>

          <div class="flex flex-col gap-2">
            <Label
              for="enhance-retention"
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Retention
            </Label>
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Select.Root
                type="single"
                value={String(enhanceRetention)}
                onValueChange={(v) => {
                  const n = Number(v)
                  if (CACHE_RETENTION_OPTIONS.some((o) => o.value === n)) {
                    enhanceRetention = n as ImageEnhanceRetentionHours
                    imageEnhanceSaved = false
                  }
                }}
                disabled={savingImageEnhance || !enhanceEnabled}
              >
                <Select.Trigger id="enhance-retention" class="h-9 w-full sm:w-[14rem]">
                  {enhanceRetentionLabel}
                </Select.Trigger>
                <Select.Content class="z-62">
                  {#each CACHE_RETENTION_OPTIONS as opt (opt.value)}
                    <Select.Item value={String(opt.value)} label={opt.label}>
                      {opt.label}
                    </Select.Item>
                  {/each}
                </Select.Content>
              </Select.Root>
              <Button
                size="sm"
                variant="outline"
                class="shrink-0 gap-1.5 {imageEnhanceDirty ? saveDirtyClass : ''}"
                disabled={savingImageEnhance || !imageEnhanceDirty}
                onclick={() => void saveImageEnhanceSettings()}
              >
                {#if imageEnhanceSaved && !savingImageEnhance && !imageEnhanceDirty}
                  <CheckIcon class="size-3.5" strokeWidth={2} />
                  Saved
                {:else}
                  Save enhance settings
                {/if}
              </Button>
            </div>
          </div>

          {#if imageEnhance?.lastPruneAt || imageEnhance?.lastPruneError}
            <div
              class="space-y-1 border-t border-latte-surface1/50 pt-3
                dark:border-macchiato-surface1/50"
            >
              {#if formatWhen(imageEnhance?.lastPruneAt)}
                <p class="text-xs text-latte-text/60 dark:text-macchiato-text/60">
                  Last prune: {formatWhen(imageEnhance?.lastPruneAt)}
                </p>
              {/if}
              {#if imageEnhance?.lastPruneError}
                <p class="text-xs text-latte-red dark:text-macchiato-red">
                  Last prune error: {imageEnhance.lastPruneError}
                </p>
              {/if}
            </div>
          {/if}

          {#if imageEnhanceError}
            <p class="text-sm text-latte-red dark:text-macchiato-red">{imageEnhanceError}</p>
          {/if}
        </div>
      </section>
    {/if}

    {#if isGatewayOwner}
      <section
        class="overflow-hidden rounded-lg border border-latte-red/35 bg-latte-mantle/70
          shadow-sm backdrop-blur-sm dark:border-macchiato-red/35 dark:bg-macchiato-mantle/70"
      >
        <div
          class="flex items-center gap-2 border-b border-latte-red/25 px-5 py-3.5
            dark:border-macchiato-red/25"
        >
          <TrashIcon
            class="size-4 shrink-0 text-latte-red dark:text-macchiato-red"
            strokeWidth={1.75}
          />
          <div class="min-w-0">
            <p
              class="text-[10px] font-light uppercase tracking-wide text-latte-text/55
                dark:text-macchiato-text/55"
            >
              Danger zone
            </p>
            <p class="text-sm font-semibold text-latte-text dark:text-macchiato-text">
              Delete server
            </p>
          </div>
        </div>
        <div class="space-y-3 px-5 py-4">
          <p class="text-sm text-latte-text/65 dark:text-macchiato-text/65">
            Remove this server from the gateway. It will no longer appear for you or
            invited members.
          </p>
          {#if deleteError}
            <p class="text-sm text-latte-red dark:text-macchiato-red">{deleteError}</p>
          {/if}
          <Button
            variant="outline"
            class="w-full gap-2 border-latte-red/40 text-latte-red hover:bg-latte-red/10
              dark:border-macchiato-red/40 dark:text-macchiato-red dark:hover:bg-macchiato-red/10"
            onclick={() => {
              deleteError = null
              confirmDeleteOpen = true
            }}
          >
            <TrashIcon class="size-4" strokeWidth={1.75} />
            Delete server
          </Button>
        </div>
      </section>
    {/if}
  {/if}
</div>

<AlertDialog.Root bind:open={confirmDeleteOpen}>
  <AlertDialog.Content
    class="border-latte-surface1/60 bg-latte-mantle/95
      dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/95"
  >
    <AlertDialog.Header>
      <AlertDialog.Title class="text-latte-text dark:text-macchiato-text">
        Delete server?
      </AlertDialog.Title>
      <AlertDialog.Description class="text-latte-text/65 dark:text-macchiato-text/65">
        This will permanently remove
        <strong class="text-latte-text dark:text-macchiato-text">
          {formatServerName(displayName, serverUid)}
        </strong>
        from the gateway. Members will lose access. This action cannot be undone.
      </AlertDialog.Description>
    </AlertDialog.Header>
    <AlertDialog.Footer>
      <AlertDialog.Cancel disabled={deleting}>Cancel</AlertDialog.Cancel>
      <AlertDialog.Action
        class={buttonVariants({ variant: 'destructive' })}
        disabled={deleting}
        onclick={(e) => {
          e.preventDefault()
          void confirmDelete()
        }}
      >
        {deleting ? 'Deleting…' : 'Delete server'}
      </AlertDialog.Action>
    </AlertDialog.Footer>
  </AlertDialog.Content>
</AlertDialog.Root>
